import React, { useState, useEffect } from "react";
import GridTable from "cvmgmt-cube-grid-table";
import { useParams, useNavigate } from "react-router-dom";
import RoleActivateDeactivateDialog from "../Modals/RoleActivateDeactivateDialog";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import { getRoleSetupTableData } from "../../services/RequestAccess/RequestAccess";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import { Pagination } from "cprime-foundational-components";
import { formatFilterValue } from "../../utils/formatFilters";
import { GridTableDataType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
import {
  setCurrentRoleId,
  updateNamesData,
  updatecloneRoleData,
} from "../../store/slices/CreateRoleSlice";
import { Box } from "@mui/material";
import { useIdToken } from "../../hooks/useAuthSession";

interface RoleSetupProps {
  setTabValue: (value: number) => void;
  isEditable?: boolean;
}

const RoleSetup: React.FC<RoleSetupProps> = ({ setTabValue, isEditable }) => {
  const idToken = useIdToken();
  const { id } = useParams();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [gridData, setGridData] = useState<GridTableDataType | any>();
  const [namesData, setNamesData] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const tableRefresh = useSelector(
    (state: RootState) => state.apiDetails.tableRefresh,
  );

  const roleStatus = useSelector(
    (state: RootState) => state?.createRole?.status,
  );

  useEffect(() => {
    fetchData();
  }, [filters, sorting, pagination, roleStatus]);

  const fetchData = async () => {
    const appId = id?.split("-")[1];
    setIsLoading(true);
    const resp = await getRoleSetupTableData(
      {
        sortingFilter: {
          filter:
            filters?.length > 0
              ? filters.map((item: any) =>
                  formatFilterValue(item.value, item.id),
                )
              : [],
          sort: sorting.map((item: any) => {
            return { columnName: item.id, type: item.desc ? "DESC" : "ASC" };
          }),
          page: {
            limit: pagination.rowsPerPage,
            offset: pagination?.page * pagination?.rowsPerPage,
          },
        },
      },
      appId,
    );
    resp.data.rows = resp?.data?.rows?.map((row: any) => {
      return {
        ...row,
        action: row?.action
          ?.filter((action: any) => action?.value && action?.value !== "Edit")
          .map((action: any) => {
            return {
              ...action,
              disabled: isEditable
                ? action?.disabled
                : action?.disabled || action?.value === "Edit",
            };
          }),
      };
    });
    resp.data.columns = resp?.data?.columns?.map((column: any) => {
      return {
        ...column,
        size: column?.size ?? 200,
        minSize: column?.minSize ?? 200,
      };
    });
    setGridData(resp?.data);
    setNamesData(resp?.data?.namesData);
    setIsLoading(false);
  };

  useEffect(() => {
    setTabValue(3);
  });

  const handleRowClick = (data: any) => {
    setSelectedRowData(data.row.original);
    setDialogOpen(true);
  };

  const handleEditRoleRowClick = (data: any) => {
    const appId = id?.split("-")[1];
    const rowdata = data?.original;
    const namesRes = filterNamesDataByRoleId(rowdata, namesData);
    dispatch(setCurrentRoleId(rowdata?.role_id));

    const payload = {
      roleName: rowdata?.friendly_role_name,
      objectId: rowdata?.objectid,
      sailpointRoleName: rowdata?.idp_role,
      regions: namesRes?.regions,
      fleets: namesRes?.fleets,
      dealers: namesRes?.dealers,
      distributions: namesRes?.distributions,
      plants: namesRes?.plants,
      lifecycleStatuses: namesRes?.lifecycleStatuses,
      description: rowdata?.description,
      status: rowdata?.status,
      isAutoApprove: rowdata?.isautoapprove,
    };

    dispatch(updateNamesData(payload));
    navigate(
      `/application-list/app-${appId}/role/edit-role/role-${rowdata?.role_id}`,
    );
  };

  function filterNamesDataByRoleId(rowdata: any, namesData: any) {
    const roleId = String(rowdata?.role_id);

    const categories = [
      "regions",
      "fleets",
      "dealers",
      "distributions",
      "plants",
      "lifecycleStatuses",
    ];

    const filteredNamesData: any = {};

    categories.forEach((category) => {
      const categoryData = namesData[category];

      if (categoryData && categoryData.hasOwnProperty(roleId)) {
        filteredNamesData[category] = {
          [roleId]: categoryData[roleId],
        };
      }
    });

    return filteredNamesData;
  }

  return (
    <>
      <Box sx={{ width: 1, bgcolor: "white", borderRadius: "4px" }}>
        {idToken && (
          <>
            <GridTable
              style={{
                height: `calc(100vh - ${!isEditable ? 36.2 : 34}rem)`,
                maxWidth: "calc(100vw - 4rem)",
              }}
              customPayload={true}
              isCustomizeColumns={false}
              title="Roles"
              data={gridData}
              isPagination={true}
              isFiltering={true}
              isSorting={true}
              filters={filters}
              sorting={sorting}
              setSorting={setSorting}
              setFilters={setFilters}
              refresh={tableRefresh}
              cellActions={[
                {
                  event: "Deactivate",
                  callback: (_event, data) => {
                    handleRowClick(data);
                  },
                },
                {
                  event: "Activate",
                  callback: (_event, data) => {
                    handleRowClick(data);
                  },
                },
                {
                  event: "Edit",
                  callback: (_event, data) => {
                    // const row = data?.row;
                    // handleEditRoleRowClick(row);
                    // const payload = {
                    //   isClone: false,
                    //   is_active: false,
                    //   sourceROleId: null,
                    // };
                    // dispatch(updatecloneRoleData(payload as any));
                  },
                },
              ]}
              onRowClick={(_event: any, row: any) => {
                // if (isEditable) return;
                handleEditRoleRowClick(row);
                const payload = {
                  isClone: false,
                  is_active: false,
                  sourceROleId: null,
                };
                dispatch(updatecloneRoleData(payload as any));
              }}
              showLoader={false}
              loading={isLoading}
            />
            <Pagination
              sx={{ width: "100%" }}
              {...{
                ...pagination,
                count: gridData?.totalCount ?? 0,
                rowsPerPageOptions: [25, 50, 100, 250],
                onPageChange: (e, number) => {
                  setPagination({ ...pagination, page: number });
                },
                onRowsPerPageChange: (evt) => {
                  setPagination({
                    page: 0,
                    rowsPerPage: Number(evt.target.value),
                  });
                },
              }}
            />
          </>
        )}

        {dialogOpen && (
          <RoleActivateDeactivateDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            data={selectedRowData}
          />
        )}
      </Box>
    </>
  );
};

export default RoleSetup;
